<script module>
	export const AddressTypes = {
		Residential: "Residential",
		Mailing: "Mailing",
	};
</script>

<script>
	import { onDestroy, onMount } from "svelte";
	import * as Yup from "yup";
	import { imask } from "../../common/actions/imask";
	import { useValidations } from "../../../../../SharedSvelte/Forms.svelte";
	import { baseUrl } from "../../../../../SharedSvelte/Common.svelte";
	import { Loader } from "../../../../../SharedSvelte/GlobalLoader.svelte";
	import Dialog from "../../../../../SharedSvelte/Dialog2.svelte";
	import {
		FormGroup,
		Input,
		Icon,
		Card,
		CardBody,
		CardHeader,
		CardSubtitle,
		CardText,
	} from "@sveltestrap/sveltestrap";
	import { States } from "./StateCodesList.svelte";
    
	let prompt;

	let {
		addressType = AddressTypes.Residential,
		address1,
		address2,
		city,
		usState,
		zip,
		disabled,
	} = $props();

	const { validateField, validateForm, getErrors, resetErrors, removeError } = useValidations();

	let formData = $state({
		address1: address1,
		address2: address2,
		city: city,
		state: usState,
		zip: zip,
	});

	let zipInputRef;
    let zipMaskAction;

	let errors = $derived(getErrors());
	let addressHasErrors = $derived(Object.keys(errors).length ? true : false);

	const requiredSchema = Yup.object().shape({
		address1: Yup.string().required("Address Line 1 is required"),
		address2: Yup.string().nullable(),
		city: Yup.string()
			.required("City is required")
			.matches(/^[A-Za-z ]{2,100}$/, "Invalid City"),
		state: Yup.string().required("State is required"),
		zip: Yup.string()
			.required("ZIP is required")
			.matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP Code"),
	});

	const optionalSchema = Yup.object().shape({
		address1: Yup.string().nullable(),
		address2: Yup.string().nullable(),
		city: Yup.string().nullable(),
		state: Yup.string().nullable(),
		zip: Yup.string().nullable(),
	});

	//Default to required schema. effect switches to optional when needed.
	let formSchema = $state(
		addressType === AddressTypes.Residential
			? requiredSchema
			: optionalSchema,
	);

	export function clearAllErrors() {
		resetErrors();
	}

	export function disableInputs(disable) {
		disabled = disable
	}

	export function getValues() {
		return formData;
	}

	export function setValues(data) {
		formData.address1 = data.address1;
		formData.address2 = data.address2;
		formData.city = data.city;
		formData.state = data.state;
		formData.zip = data.zip;
		resetErrors();
	}

	export function hasErrors() {
		return addressHasErrors;
	}

	export async function validate() {
		await validateForm(formSchema, formData);
	}

	export async function verify() {
		Loader.display();

		try {
			const response = await fetch(`${baseUrl}Address/ValidateAddress`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					__RequestVerificationToken: document.getElementsByName("__RequestVerificationToken",)[0].value,
				},
				body: JSON.stringify(formData)
			});
			
			if (!response.ok) {
				const error = `Server exception: ${response.status} ${response.statusText}`;
				console.log(error);
				throw new Error();
			}

			const json = await response.json();
			//console.log(json);
			Loader.hide();
			await promptSuggestedAddress(json);
		} catch (err) {
			console.log(err);
		} finally {
			Loader.hide();
		}
	}

	function handleChange(fieldName, fieldValue) {
		const vars = new Map();
		vars.set(fieldName, fieldValue);
		const data = Object.fromEntries(vars);
		for (const key in data) {
			validateField(formSchema, key, formData);
		}
	}

	/*function handleZipInput(event) {
		const input = event.target;
		formData.zip = maskNumericValue(
			input,
			event.inputType,
			"99999-9999",
			formData.zip,
		);
	}*/

	function promptSuggestedAddress(verifyResult){
			return new Promise((resolve) => {
				const json = verifyResult;
				let suggestedAddress = json?.address?.address1;
				if (suggestedAddress && json?.address?.isDifferent){
					if (json.address.address2) suggestedAddress += " <br />" + json.address.address2;
					suggestedAddress += " <br />" + json.address.city + ", " + json.address.state + " " + json.address.zipcode;
					prompt.dialog({
						title: "Address Confirmation",
						message: "The suggested address is different than what you entered. Do you want to use the following suggested address? <br /><br />" + suggestedAddress,
						okText: "Use Suggested Address",
						cancelText: "Use Original Address",
						callback: () => (async () => {
								formData.address1 = json.address.address1;
								formData.address2 = json.address.address2;
								formData.city = json.address.city;
								formData.state = json.address.state;
								formData.zip = json.address.zipcode;
								resolve(true);
							})(),
						cancelCallback: () => (async () => {
								console.log("cancel clicked");
								resolve(true);
							})()
					}
					);
				} else {
					resolve(true);
				}
			})
		}

	$effect(() => {
		if (addressType === AddressTypes.Mailing) {
			//console.log("clearing errors if needed");
			if (
				!formData.address1 &&
				!formData.address2 &&
				!formData.city &&
				!formData.state &&
				!formData.zip
			) {
				resetErrors();
			}
		}
		formSchema =
			addressType === AddressTypes.Residential
				? requiredSchema
				: formData.address1 ||
					  formData.address2 ||
					  formData.city ||
					  formData.state ||
					  formData.zip
					? requiredSchema
					: optionalSchema;
	});

	onMount(() => {
		zipMaskAction = imask(zipInputRef, {
            mask: "00000[-0000]",
            lazy: true,
        });
	});

	onDestroy(() => {
		zipMaskAction?.destroy();	
	});
</script>

<Card>
	<CardHeader>
		{addressType} Address
	</CardHeader>
	<CardBody>
		<CardText>
			<!-- <pre>{JSON.stringify(formData)}</pre> -->
			<div class="row">
				<div class="col">
					<FormGroup floating spacing="mb-3" label="Address Line 1">
						<Input
							placeholder="Address Line 1"
							type="text"
							bind:value={formData.address1}
							onchange={(e) => handleChange("address1", e.target.value)}
							invalid={Boolean(errors.address1)}
							feedback={errors.address1}
							disabled={disabled}
						/>
					</FormGroup>
				</div>
			</div>

			<div class="row">
				<div class="col">
					<FormGroup floating spacing="mb-3" label="Address Line 2">
						<Input
							placeholder="Address Line 2"
							type="text"
							bind:value={formData.address2}
							onchange={(e) => handleChange("address2", e.target.value)}
							invalid={Boolean(errors.address2)}
							feedback={errors.address2}
							disabled={disabled}
						/>
					</FormGroup>
				</div>
			</div>

			<div class="row">
				<div class="col-sm-12 col-md-4">
					<FormGroup floating spacing="mb-3" label="City">
						<Input
							placeholder="City"
							type="text"
							bind:value={formData.city}
							onchange={(e) => handleChange("city", e.target.value)}
							invalid={Boolean(errors.city)}
							feedback={errors.city}
							disabled={disabled}
						/>
					</FormGroup>
				</div>

				<div class="col-sm-12 col-md-4">
					<FormGroup floating spacing="mb-3" label="State">
						<Input
							placeholder="State"
							type="select"
							bind:value={formData.state}
							onchange={(e) => handleChange("state", e.target.value)}
							invalid={Boolean(errors.state)}
							feedback={errors.state}
							disabled={disabled}
						>
							<option value=""></option>
							{#each States as state}
								<option value={state.value}>{state.text}</option>
							{/each}
					</Input>
					</FormGroup>
				</div>

				<div class="col-sm-12 col-md-4">
					<FormGroup floating spacing="mb-3" label="ZIP">
						<Input
							placeholder="ZIP"
							type="text"
							value={formData.zip}
							bind:inner={zipInputRef}
							onchange={(e) => handleChange("zip", e.target.value)}
							onaccept={(e) => formData.zip = e.detail.value}
							invalid={Boolean(errors.zip)}
							feedback={errors.zip}
							disabled={disabled}
						/>
					</FormGroup>
				</div>
			</div>
		</CardText>
	</CardBody>
</Card>

<Dialog bind:this={prompt} />